import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, FlaskConical, FileText, Microscope, Lock, MessageCircle } from "lucide-react";
import { useAuth } from "../lib/auth";

const FLOATING_VIALS = [
    { src: "/products/glutathione.png", className: "float-1", w: 240, top: "10%", right: "8%", z: 3 },
    { src: "/products/tb500.png",        className: "float-2", w: 170, top: "32%", right: "32%", z: 2 },
    { src: "/products/ghkcu.png",        className: "float-3", w: 150, top: "58%", right: "12%", z: 2 },
    { src: "/products/nad.png",          className: "float-2", w: 130, top: "8%",  right: "38%", z: 1 },
];

const FAQS = [
    { q: "How is purity verified?",
       a: "Every Aminoplex lot is analyzed by an independent third-party laboratory using HPLC + ESI Mass Spectrometry for both identity confirmation and quantitative purity. Acceptance threshold is ≥99%." },
    { q: "Are batch-specific COAs included?",
       a: "Yes. Every shipment ships with the matching Certificate of Analysis for its specific lot. The entire COA archive is also publicly searchable in our COA Library." },
    { q: "Which payment methods are accepted?",
       a: "Zelle bank-to-bank transfer (US only). All orders are processed under a payment-before-ship policy." },
    { q: "How are products stored and shipped?",
       a: "Lyophilized peptides ship at ambient temperature with desiccant. Recipients should store vials at -20°C until reconstitution; reconstituted material is refrigerated at 2-8°C." },
];

export default function HomePage() {
    const { user } = useAuth();
    return (
        <div data-testid="home-page" className="bg-[var(--bg)]">
            <HeroGate user={user} />
            <GuaranteeSection />
            <ProcessSection />
            <CategoryStrip />
            <FaqSection />
            <DiscordCta />
        </div>
    );
}

function HeroGate({ user }) {
    const navigate = useNavigate();
    return (
        <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden">
            <div className="absolute inset-0 hero-gradient" />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-6 relative z-10 reveal">
                    <div className="inline-flex items-center gap-2 pill mb-7">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] pulse-gold" />
                        <span className="font-ui font-medium text-[var(--text-secondary)]">Researchers verified · USA based</span>
                    </div>

                    {user ? (
                        <>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-7">
                                Welcome back,<br />
                                <span className="text-[var(--brand)]">{user.name?.split(" ")[0] || "researcher"}.</span>
                            </h1>
                            <p className="text-lg text-secondary max-w-lg leading-relaxed mb-10">
                                The full Aminoplex catalog is available to you. Browse, reorder favorites,
                                or review your Certificates of Analysis at any time.
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                <Link to="/shop" className="btn btn-solid" data-testid="hero-shop-cta">
                                    Browse Catalog <ArrowRight size={15} />
                                </Link>
                                <Link to="/coa" className="btn">View COA Library</Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-7">
                                Researcher sign-in<br />
                                <span className="text-[var(--brand)]">required.</span>
                            </h1>
                            <p className="text-lg text-secondary max-w-lg leading-relaxed mb-10">
                                Create an Aminoplex account to view our research peptide catalog. Every lot
                                is third-party verified to ≥99% purity with complete batch documentation.
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                <Link to="/register" className="btn btn-solid" data-testid="hero-register-cta">
                                    Get Started <ArrowRight size={15} />
                                </Link>
                                <Link to="/login" className="btn" data-testid="hero-login-cta">Sign In</Link>
                            </div>
                            <div className="mt-10 flex items-center gap-6 text-sm text-muted font-ui">
                                <span className="flex items-center gap-2"><ShieldCheck size={14} /> ≥99% Purity</span>
                                <span className="flex items-center gap-2"><FileText size={14} /> Batch COA</span>
                                <span className="flex items-center gap-2"><FlaskConical size={14} /> HPLC + MS</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Floating vials column */}
                <div className="lg:col-span-6 relative h-[500px] lg:h-[640px]">
                    {FLOATING_VIALS.map((v, i) => (
                        <img
                            key={i}
                            src={v.src}
                            alt=""
                            loading="eager"
                            className={`absolute object-contain ${v.className}`}
                            style={{ width: v.w, top: v.top, right: v.right, zIndex: v.z,
                                       filter: "drop-shadow(0 25px 40px rgba(15, 23, 42, 0.15))" }}
                        />
                    ))}
                </div>
            </div>

            {/* Marquee strip */}
            <div className="relative mt-16 disclaimer-band overflow-hidden">
                <div className="marquee-track flex gap-12 whitespace-nowrap">
                    {[...Array(2)].map((_, k) => (
                        <div key={k} className="flex gap-12 items-center px-6">
                            {[
                                "≥99% PURITY VERIFIED",
                                "HPLC + ESI MASS SPECTROMETRY",
                                "INDEPENDENT THIRD-PARTY LABORATORY",
                                "BATCH-SPECIFIC COA INCLUDED",
                                "FOR LABORATORY RESEARCH USE ONLY",
                                "ZELLE · WIRE",
                            ].map((t) => (
                                <span key={t} className="font-ui font-medium text-xs tracking-[0.18em] text-[var(--text-secondary)] flex items-center gap-3">
                                    <span className="w-1 h-1 bg-[var(--brand)] rounded-full" />
                                    {t}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function GuaranteeSection() {
    return (
        <section className="py-24 md:py-32 bg-[var(--bg)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] mb-6">
                        The Aminoplex<br/><span className="text-[var(--brand)]">guarantee.</span>
                    </h2>
                    <p className="text-secondary leading-relaxed text-lg mb-8 max-w-lg">
                        Documented quality for research and laboratory use. Every batch meets our internal purity standards and ships with primary analytical data.
                    </p>
                    <Link to="/quality" className="btn btn-outline">Learn more <ArrowRight size={15} /></Link>
                </div>
                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                        { tile: "tile-blue", title: "Third-Party Verified", body: "Independent laboratory testing on every batch — no internal-only data.", Icon: ShieldCheck },
                        { tile: "tile-pink", title: "HPLC + Mass Spec", body: "Identity confirmed by ESI-MS; purity quantified by RP-HPLC at 220 nm.", Icon: Microscope },
                        { tile: "tile-green", title: "Batch COA Included", body: "Every shipment ships with the matching Certificate of Analysis for its lot.", Icon: FileText },
                        { tile: "tile-cream", title: "Research-Grade Sourcing", body: "ISO-aligned synthesis with full traceability from synthesis to package.", Icon: FlaskConical },
                    ].map((c, i) => (
                        <div key={i} className={`p-8 rounded-[22px] ${c.tile} lift`}>
                            <c.Icon size={22} className="text-[var(--text-primary)] mb-5" />
                            <h3 className="font-display text-2xl mb-2">{c.title}</h3>
                            <p className="text-sm text-secondary leading-relaxed">{c.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ProcessSection() {
    const steps = [
        { n: "01", title: "Synthesis", desc: "Solid-phase synthesis under ISO-aligned protocols." },
        { n: "02", title: "Purification", desc: "Multi-stage RP-HPLC purification & fraction analysis." },
        { n: "03", title: "Analysis", desc: "Independent third-party HPLC + ESI Mass Spectrometry." },
        { n: "04", title: "Documentation", desc: "Batch-specific COA archived & shipped with order." },
    ];
    return (
        <section className="py-24 md:py-32 bg-[var(--bg-surface)] border-y border-[var(--border)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-16 max-w-2xl">
                    <div className="data-label mb-3">QUALITY CONTROL</div>
                    <h2 className="font-display text-4xl md:text-5xl leading-tight mb-4">
                        Four steps. Zero shortcuts.<br/>
                        <span className="text-[var(--brand)]">Every single batch.</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {steps.map((s) => (
                        <div key={s.n} className="card p-8 lift">
                            <div className="font-display text-6xl text-[var(--brand)] opacity-25 mb-5">{s.n}</div>
                            <h3 className="font-display text-2xl mb-2">{s.title}</h3>
                            <p className="text-sm text-secondary leading-relaxed">{s.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CategoryStrip() {
    const { user } = useAuth();
    return (
        <section className="py-24 md:py-32 bg-[var(--bg)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="data-label mb-3">EXPLORE CATEGORIES</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-tight">
                            Built for serious research.
                        </h2>
                    </div>
                    {user && <Link to="/shop" className="btn btn-outline">View catalog <ArrowRight size={15} /></Link>}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {[
                        { name: "Recovery", img: "/products/tb500.png", tile: "tile-blue" },
                        { name: "Metabolic", img: "/products/ap3-10mg.png", tile: "tile-pink" },
                        { name: "Cellular", img: "/products/nad.png", tile: "tile-cream" },
                        { name: "Nootropic", img: "/products/selank.png", tile: "tile-green" },
                        { name: "Dermal", img: "/products/ghkcu.png", tile: "tile-lavender" },
                    ].map((c) => (
                        <Link
                            to={user ? `/shop?category=${c.name}` : "/register"}
                            key={c.name}
                            className={`group rounded-[22px] ${c.tile} aspect-square overflow-hidden relative p-6 flex flex-col justify-between lift`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-display text-xl">{c.name}</span>
                                {!user && <Lock size={14} className="text-[var(--text-muted)]" />}
                            </div>
                            <img src={c.img} alt="" className="w-full h-32 object-contain transition-transform duration-500 group-hover:scale-110" />
                        </Link>
                    ))}
                </div>

                {!user && (
                    <div className="mt-16 card p-10 lg:p-14 text-center max-w-3xl mx-auto reveal">
                        <Lock size={22} className="mx-auto text-[var(--brand)] mb-4" />
                        <div className="data-label mb-3 text-[var(--brand)]">CATALOG ACCESS</div>
                        <h3 className="font-display text-3xl md:text-4xl mb-4">Sign in to view our research peptide catalog.</h3>
                        <p className="text-secondary mb-7 max-w-lg mx-auto">
                            Aminoplex is a researcher-only marketplace. Pricing, stock, and Certificates of Analysis are visible after sign-in.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                            <Link to="/register" className="btn btn-solid" data-testid="cta-register">Create Account</Link>
                            <Link to="/login" className="btn" data-testid="cta-login">Sign In</Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function FaqSection() {
    return (
        <section className="py-24 md:py-32 bg-[var(--bg-surface)] border-t border-[var(--border)]">
            <div className="max-w-4xl mx-auto px-6 lg:px-10">
                <div className="mb-12">
                    <div className="data-label mb-3">FAQ</div>
                    <h2 className="font-display text-4xl md:text-5xl leading-tight">Researcher questions.</h2>
                </div>
                <div className="space-y-3">
                    {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} idx={i} />)}
                </div>
            </div>
        </section>
    );
}

function DiscordCta() {
    return (
        <section data-testid="discord-cta-section" className="py-20 md:py-28 bg-[var(--bg)]">
            <div className="max-w-4xl mx-auto px-6 lg:px-10">
                <div className="relative overflow-hidden rounded-2xl bg-[var(--bg-carbon)] border border-[var(--border)] p-10 md:p-14">
                    <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ background: "radial-gradient(circle at 70% 20%, var(--brand) 0%, transparent 50%)" }} />
                    <div className="relative">
                        <div className="data-label text-[var(--brand)] mb-4">COMMUNITY</div>
                        <h2 className="font-display text-3xl md:text-5xl leading-tight text-[var(--text-primary)]">
                            Join our research community.
                        </h2>
                        <p className="mt-5 text-secondary leading-relaxed max-w-2xl">
                            Early access to restocks and new compounds, batch Certificates of Analysis, direct support, and a community of fellow researchers — all in our Discord.
                        </p>

                        <div className="mt-7 flex flex-wrap gap-2">
                            {["Research Community", "Member-only offers", "Customer Support"].map((t) => (
                                <span key={t} className="font-ui text-xs tracking-wide text-[var(--text-secondary)] px-4 py-1.5 rounded-full border border-[var(--border)] bg-[var(--bg-surface)]">
                                    {t}
                                </span>
                            ))}
                        </div>

                        <a
                            href="https://discord.com/invite/nMRuf628q"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="join-discord-btn"
                            className="mt-9 inline-flex items-center justify-center gap-3 w-full md:w-auto px-10 py-4 rounded-full bg-[var(--brand)] text-[var(--bg-carbon)] font-ui font-semibold tracking-wide uppercase text-sm hover:brightness-110 transition"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Join our Discord
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FaqItem({ q, a, idx }) {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div className="card overflow-hidden" data-testid={`faq-item-${idx}`}>
            <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-5 p-7 text-left">
                <span className="font-ui font-semibold text-sm text-[var(--brand)] mt-1">0{idx + 1}</span>
                <span className="font-display text-xl flex-1">{q}</span>
                <span className="text-[var(--brand)] text-2xl font-light">{open ? "−" : "+"}</span>
            </button>
            {open && (
                <div className="px-7 pb-7 pl-[3.7rem]">
                    <div className="hairline mb-5" />
                    <p className="text-secondary leading-relaxed">{a}</p>
                </div>
            )}
        </div>
    );
}
